import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TODAY = new Date().toISOString().split('T')[0];
const STATUS_COLORS = {
  booked:'bg-blue-100 text-blue-700',
  checked_in:'bg-green-100 text-green-700',
  checked_out:'bg-gray-100 text-gray-600',
  cancelled:'bg-red-100 text-red-600'
};
const EMPTY = {
  guest_name:'', company:'', city:'', hotel_id:'', room_number:'',
  check_in:'', check_out:'', nights:0, charges:'', paid_by:'', remarks:'', status:'booked'
};
const HOTEL_EMPTY = { name:'', city:'', address:'', phone:'' };

export default function HotelBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showHotelForm, setShowHotelForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [hotelForm, setHotelForm] = useState(HOTEL_EMPTY);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/hotel-bookings' + (filterStatus ? `?status=${filterStatus}` : '')); setBookings(r.data); }
    catch (err) { console.error('hotel-bookings:', err); setBookings([]); }
    try { const r = await api.get('/api/hotel-bookings/hotels'); setHotels(r.data); }
    catch (err) { console.error('hotels:', err); setHotels([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterStatus]);

  const getHotel = (id) => hotels.find(h => h.id === Number(id));

  // Auto-compute nights when dates change
  const handleFormChange = (updates) => {
    const newForm = { ...form, ...updates };
    if (newForm.check_in && newForm.check_out) {
      const diff = (new Date(newForm.check_out) - new Date(newForm.check_in)) / (1000*60*60*24);
      newForm.nights = diff > 0 ? diff : 0;
    }
    setForm(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, hotel_id: form.hotel_id ? Number(form.hotel_id) : null,
        nights: Number(form.nights), charges: form.charges ? Number(form.charges) : null };
      if (editing) await api.put(`/api/hotel-bookings/${editing}`, payload);
      else await api.post('/api/hotel-bookings', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (b) => {
    setForm({ guest_name:b.guest_name||'', company:b.company||'', city:b.city||'',
      hotel_id:b.hotel_id||'', room_number:b.room_number||'', check_in:b.check_in||'',
      check_out:b.check_out||'', nights:b.nights||0, charges:b.charges||'',
      paid_by:b.paid_by||'', remarks:b.remarks||'', status:b.status||'booked' });
    setEditing(b.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try { await api.delete(`/api/hotel-bookings/${id}`); load(); }
    catch { alert('Delete failed'); }
  };

  const handleAddHotel = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/hotels', hotelForm);
      setShowHotelForm(false); setHotelForm(HOTEL_EMPTY); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const exportCSV = () => {
    const headers = ['Guest','Company','City','Hotel','Room','Check In','Check Out','Nights','Charges','Status'];
    const rows = bookings.map(b => [
      b.guest_name, b.company||'', b.city||'', getHotel(b.hotel_id)?.name||'',
      b.room_number||'', b.check_in||'', b.check_out||'', b.nights||0, b.charges||0, b.status
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = 'hotel_bookings.csv'; a.click();
  };

  // Room status summary
  const checkedInCount = bookings.filter(b => b.status === 'checked_in').length;
  const bookedCount = bookings.filter(b => b.status === 'booked').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hotel Bookings</h1>
          <p className="text-gray-500 text-sm">{bookings.length} bookings</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportCSV} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">📥 Export CSV</button>
          <button onClick={() => setShowHotelForm(true)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">🏨 Add Hotel</button>
          <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ New Booking
          </button>
        </div>
      </div>

      {/* Room Status Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-sm text-green-600">Checked In</p>
          <p className="text-2xl font-bold text-green-700">{checkedInCount}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <p className="text-sm text-blue-600">Booked</p>
          <p className="text-2xl font-bold text-blue-700">{bookedCount}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-600">Checked Out</p>
          <p className="text-2xl font-bold text-gray-700">{bookings.filter(b=>b.status==='checked_out').length}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <p className="text-sm text-red-600">Cancelled</p>
          <p className="text-2xl font-bold text-red-700">{bookings.filter(b=>b.status==='cancelled').length}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex gap-3 flex-wrap">
        {[['','All'],['booked','Booked'],['checked_in','Checked In'],['checked_out','Checked Out'],['cancelled','Cancelled']].map(([val,label]) => (
          <button key={val} onClick={() => setFilterStatus(val)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterStatus===val ? 'bg-blue-700 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Add Hotel Modal */}
      {showHotelForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Add Hotel</h2>
              <button onClick={() => setShowHotelForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleAddHotel} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
                <input type="text" value={hotelForm.name} onChange={e => setHotelForm({...hotelForm,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={hotelForm.city} onChange={e => setHotelForm({...hotelForm,city:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={hotelForm.address} onChange={e => setHotelForm({...hotelForm,address:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={hotelForm.phone} onChange={e => setHotelForm({...hotelForm,phone:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">Add Hotel</button>
                <button type="button" onClick={() => setShowHotelForm(false)} className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Booking Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Booking' : 'New Booking'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
                <input type="text" value={form.guest_name} onChange={e => handleFormChange({guest_name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input type="text" value={form.company} onChange={e => handleFormChange({company:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={form.city} onChange={e => handleFormChange({city:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel</label>
                <select value={form.hotel_id} onChange={e => handleFormChange({hotel_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Hotel —</option>
                  {hotels.map(h => <option key={h.id} value={h.id}>{h.name} ({h.city||''})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Number</label>
                <input type="text" value={form.room_number} onChange={e => handleFormChange({room_number:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charges (Rs)</label>
                <input type="number" value={form.charges} onChange={e => handleFormChange({charges:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                <input type="date" value={form.check_in} onChange={e => handleFormChange({check_in:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                <input type="date" value={form.check_out} onChange={e => handleFormChange({check_out:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nights (auto)</label>
                <input type="number" value={form.nights} readOnly
                  className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paid By</label>
                <input type="text" value={form.paid_by} onChange={e => handleFormChange({paid_by:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => handleFormChange({status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="booked">Booked</option>
                  <option value="checked_in">Checked In</option>
                  <option value="checked_out">Checked Out</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea value={form.remarks} onChange={e => handleFormChange({remarks:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Booking'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400"><div className="text-4xl animate-pulse mb-2">🏨</div><p>Loading...</p></div>
        ) : bookings.length === 0 ? (
          <div className="p-12 text-center text-gray-400"><div className="text-5xl mb-3">🏨</div><p>No bookings found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Guest','Company','City','Hotel','Room','Check In','Check Out','Nights','Charges','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bookings.map(b => (
                  <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{b.guest_name}</td>
                    <td className="px-4 py-3 text-gray-600">{b.company||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.city||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{getHotel(b.hotel_id)?.name||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.room_number||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.check_in||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.check_out||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.nights||0}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">Rs {Number(b.charges||0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[b.status]||'bg-gray-100 text-gray-500'}`}>
                        {b.status?.replace('_',' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(b)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️</button>
                        <button onClick={() => handleDelete(b.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
